/**
 * src/lib/services/reservations.ts
 * Stock reservation service — wired to inventory-service's real
 * reservation endpoints (Finding 3 / manual-hold workaround).
 *
 * Backend responses are snake_case (see glow-ai-inventory-service's
 * app/schemas/reservation.py); the map* helpers below convert them to the
 * camelCase StockReservation / ReservationLineItem types the rest of the
 * dashboard already uses.
 */
import type {
  ReservationLineItem,
  ReservationStatus,
  StockReservation,
  SubstituteCandidate,
} from '@/lib/types/workflow';
import { post, patch, del, get, ApiError } from '@/lib/api/client';

type BackendReservationLine = {
  id: string;
  gear_item_id: string;
  name: string;
  category: string | null;
  quantity: number;
  unit_price: number;
  reservation_status: string;
  substitute_available: boolean;
  substitute_name: string | null;
  substitute_unit_price: number | null;
  expires_at: string;
  rms_hold_id: string | null;
};

type BackendReservation = {
  id: string;
  booking_id: string;
  status: string;
  lines: BackendReservationLine[];
  reserved_at: string;
  expires_at: string;
  all_held: boolean;
  requires_substitution: boolean;
  rms_hold_reference: string | null;
  confirmed_by_id: string | null;
  confirmed_at: string | null;
  current_rms_web_url: string | null;
};

function mapLine(l: BackendReservationLine): ReservationLineItem {
  return {
    gearItemId: l.gear_item_id,
    name: l.name,
    category: l.category ?? undefined,
    quantity: l.quantity,
    unitPrice: l.unit_price,
    reservationStatus: l.reservation_status as ReservationStatus,
    substituteAvailable: l.substitute_available,
    substituteName: l.substitute_name ?? undefined,
    substituteUnitPrice: l.substitute_unit_price ?? undefined,
    expiresAt: l.expires_at,
    rmsHoldId: l.rms_hold_id ?? undefined,
  };
}

function mapReservation(r: BackendReservation): StockReservation {
  return {
    id: r.id,
    bookingId: r.booking_id,
    lines: r.lines.map(mapLine),
    reservedAt: r.reserved_at,
    expiresAt: r.expires_at,
    allHeld: r.all_held,
    requiresSubstitution: r.requires_substitution,
    currentRmsWebUrl: r.current_rms_web_url ?? undefined,
  };
}

export type ReserveStockItem = {
  gearItemId: string;
  name: string;
  quantity: number;
  unitPrice: number;
};

export async function reserveStock(
  bookingId: string,
  items: ReserveStockItem[],
  startDate: string,
  endDate: string,
): Promise<StockReservation> {
  const result = await post<BackendReservation>('/api/v1/reservations', {
    booking_id: bookingId,
    items: items.map((i) => ({
      gear_item_id: i.gearItemId,
      name: i.name,
      quantity: i.quantity,
      unit_price: i.unitPrice,
    })),
    start_date: startDate,
    end_date: endDate,
  });
  return mapReservation(result);
}

/**
 * Fetches the existing StockReservation for a booking, if one has already
 * been created (e.g. on returning to a booking already in the
 * STOCK_RESERVATION/FINALIZE stage after a page reload). Returns null when
 * no reservation exists yet for this booking (backend 404) rather than
 * throwing, so callers can treat "no reservation yet" as a normal state.
 */
export async function getReservationByBooking(bookingId: string): Promise<StockReservation | null> {
  try {
    const result = await get<BackendReservation>(`/api/v1/reservations/by-booking/${bookingId}`);
    return mapReservation(result);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
}

export async function confirmSubstitution(
  reservationId: string,
  lineItemId: string,
  acceptSubstitute: boolean,
  manualSubstitute?: { name: string; unitPrice: number },
): Promise<ReservationLineItem> {
  const result = await patch<BackendReservationLine>(
    `/api/v1/reservations/${reservationId}/lines/${lineItemId}`,
    {
      accept_substitute: acceptSubstitute,
      ...(manualSubstitute && {
        manual_substitute_name: manualSubstitute.name,
        manual_substitute_unit_price: manualSubstitute.unitPrice,
      }),
    },
  );
  return mapLine(result);
}

/**
 * Records that the operator has manually placed the stock hold in Current
 * RMS's own UI (manual-hold workaround — see inventory-service's
 * app/models/reservation.py). Blocked (409) server-side while any line
 * still has an unresolved substitution decision pending.
 */
export async function confirmHold(
  reservationId: string,
  actorId: string,
  rmsHoldReference: string,
): Promise<StockReservation> {
  const result = await post<BackendReservation>(
    `/api/v1/reservations/${reservationId}/confirm-hold`,
    { actor_id: actorId, rms_hold_reference: rmsHoldReference },
  );
  return mapReservation(result);
}

export async function releaseReservation(reservationId: string): Promise<void> {
  await del<void>(`/api/v1/reservations/${reservationId}`);
}

type BackendSubstituteCandidate = {
  id: string;
  name: string;
  day_rate: number | null;
  quantity_available: number;
};

type BackendSubstituteCandidatesResponse = {
  category: string | null;
  candidates: BackendSubstituteCandidate[];
};

/**
 * Live Current RMS products matching `query` and/or the line's own
 * category — populates the operator's substitute-selection dropdown in
 * StockReservationPanel.tsx. Read-only (GET /products under the hood);
 * never places or modifies anything in Current RMS.
 *
 * Two independent, operator-triggered filters (see
 * [[finding-substitute-dropdown-combined-search-and-browse]], 28 Sep
 * 2026): typed free-text (`query`, searches every category) and
 * `browseCategory` (an explicit "Browse {category} items" click, filtered
 * to the line's own stored Current RMS category). Both can be blank/false
 * — the caller should only invoke this once the operator has actually
 * typed something (debounced, 2+ characters) or clicked the browse
 * toggle; passing neither returns [] without calling the backend at all,
 * matching the backend's own "never auto-populate" behavior
 * (see [[finding-substitute-dropdown-manual-search-not-automatic]],
 * 18 Sep 2026 — automatic category pre-filtering on load is what that
 * finding removed; an operator-clicked browse toggle is a different,
 * still-manual thing).
 */
export async function getSubstituteCandidates(
  reservationId: string,
  lineItemId: string,
  query: string,
  browseCategory = false,
): Promise<SubstituteCandidate[]> {
  const q = query.trim();
  if (!q && !browseCategory) return [];
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (browseCategory) params.set('browse_category', 'true');
  const result = await get<BackendSubstituteCandidatesResponse>(
    `/api/v1/reservations/${reservationId}/lines/${lineItemId}/substitute-candidates?${params.toString()}`,
  );
  return result.candidates.map((c) => ({
    id: c.id,
    name: c.name,
    dayRate: c.day_rate,
    quantityAvailable: c.quantity_available,
  }));
}
